import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Container } from '../../../../types/canvas.types';
import { DragIcon, MultiChoiceIcon } from 'assests/GmsIcons';
import { OverFlowIcon } from 'assests/GmsIcons';
import { DropdownMenu } from '../../../Common/DropdownMenu';
import { WidgetIcon, ContainerIcon, DeleteIcon } from '../utils/containerIcons';
import { getItemDisplayName, getItemTypeLabel, truncateName } from '../utils/containerHelpers';

interface NestedContainerItemProps {
  nestedContainer: Container;
  level?: number;
  onItemClick: (item: any, containerId: string) => void;
  onNestedContainerClick: (nestedContainerId: string) => void;
  onSubmodelClick: (submodelId: string, submodelName: string) => void;
  onDeleteComponent: (item: any, containerId: string) => void;
  onDeleteNestedContainer: (nestedContainer: Container) => void;
  dragHandleProps?: any;
  isDragging?: boolean;
  onItemDragEnd?: (result: DropResult, containerId: string) => void;
}

export const NestedContainerItem: React.FC<NestedContainerItemProps> = ({
  nestedContainer,
  level = 1,
  onItemClick,
  onNestedContainerClick,
  onSubmodelClick,
  onDeleteComponent,
  onDeleteNestedContainer,
  dragHandleProps,
  isDragging,
  onItemDragEnd
}) => {
  const [isExpanded, setIsExpanded] = useState(level === 1);

  return (
    <div className={`tw-ml-${Math.min(level * 4, 12)} ${isDragging ? 'tw-bg-blue-50 tw-rounded tw-p-2' : ''}`}>
      {/* Nested Container Header */}
      <div
        className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded tw-cursor-pointer hover:tw-bg-gray-50 tw-group"
        onClick={() => onNestedContainerClick(nestedContainer.id)}
      >
        {dragHandleProps && (
          <div {...dragHandleProps} className="tw-cursor-move">
            <DragIcon />
          </div>
        )}
        <button
          className="tw-p-0 tw-border-none tw-bg-transparent"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          <svg
            className={`tw-w-3 tw-h-3 tw-transition-transform ${isExpanded ? 'tw-rotate-90' : ''}`}
            viewBox="0 0 16 16"
          >
            <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </button>
        <ContainerIcon />
        <span
          className="tw-text-sm tw-text-[#172B4D] tw-flex-1 tw-truncate"
          title={nestedContainer.name}
        >
          {truncateName(nestedContainer.name)}
        </span>
        <div className="tw-opacity-0 tw-group-hover:tw-opacity-100 tw-transition-opacity">
          <DropdownMenu
            trigger={<OverFlowIcon />}
            position="right"
            items={[
              {
                id: 'delete',
                label: 'Delete',
                icon: <DeleteIcon />,
                onClick: () => onDeleteNestedContainer(nestedContainer),
                className: 'tw-text-red-600 hover:tw-bg-red-50'
              }
            ]}
          />
        </div>
      </div>

      {/* Nested Container Contents with Drag and Drop */}
      {isExpanded && (
        <DragDropContext onDragEnd={(result) => onItemDragEnd?.(result, nestedContainer.id)}>
          <div className="tw-ml-4 tw-mt-1">
            
            {/*  nested container's components with drag and drop */}
            {nestedContainer.content.items && nestedContainer.content.items.length > 0 && (
              <Droppable droppableId={`nested-${nestedContainer.id}`} type="nested-component">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`tw-space-y-1 tw-ps-3 tw-pb-2 ${
                      snapshot.isDraggingOver ? 'tw-bg-blue-50 tw-rounded' : ''
                    }`}
                  >
                    {nestedContainer?.content?.items?.map((item: any, index: number) => {
                      const displayName = getItemDisplayName(item);
                      const typeLabel = getItemTypeLabel(item);
                      const fullName = `${displayName} ${typeLabel}`;

                      return (
                        <Draggable
                          key={item.id}
                          draggableId={`nested-item-${item.id}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-rounded tw-cursor-pointer hover:tw-bg-gray-50 tw-group tw-text-sm ${
                                snapshot.isDragging ? 'tw-bg-blue-100 tw-shadow-md' : ''
                              }`}
                              onClick={() => onItemClick(item, nestedContainer.id)}
                            >
                              <div {...provided.dragHandleProps} className="tw-cursor-move">
                                <DragIcon />
                              </div>
                              <div className="tw-flex-shrink-0">
                                {item.type === 'widget' ? <WidgetIcon /> : <MultiChoiceIcon />}
                              </div>
                              <div className="tw-flex-1 tw-min-w-0">
                                <span
                                  className="tw-text-[#172B4D] tw-block tw-truncate"
                                  title={fullName}
                                >
                                  {truncateName(displayName, 20)}
                                  {typeLabel && (
                                    <span className="tw-text-xs tw-text-[#97A0AF] tw-ml-1">
                                      {typeLabel}
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className="tw-opacity-0 tw-group-hover:tw-opacity-100 tw-transition-opacity">
                                <DropdownMenu
                                  trigger={<OverFlowIcon />}
                                  position="right"
                                  items={[
                                    {
                                      id: 'delete',
                                      label: 'Delete',
                                      icon: <DeleteIcon />,
                                      onClick: () => onDeleteComponent(item, nestedContainer.id),
                                      className: 'tw-text-red-600 hover:tw-bg-red-50'
                                    }
                                  ]}
                                />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}

            {/* Recursively show deeper nested containers */}
            {nestedContainer.content.nestedContainers && nestedContainer.content.nestedContainers.length > 0 && (
              <div className="tw-mt-2">
                {nestedContainer.content.nestedContainers.map((deeperNestedContainer: Container, index: number) => (
                  <NestedContainerItem
                    key={`${deeperNestedContainer.id}-${index}`}
                    nestedContainer={deeperNestedContainer}
                    level={level + 1}
                    onItemClick={onItemClick}
                    onNestedContainerClick={onNestedContainerClick}
                    onSubmodelClick={onSubmodelClick}
                    onDeleteComponent={onDeleteComponent}
                    onDeleteNestedContainer={onDeleteNestedContainer}
                    onItemDragEnd={onItemDragEnd}
                  />
                ))}
              </div>
            )}

            {/* Empty state for nested container */}
            {!nestedContainer.content.submodel &&
            (!nestedContainer.content.items || nestedContainer.content.items.length === 0) &&
            (!nestedContainer.content.nestedContainers || nestedContainer.content.nestedContainers.length === 0) && (
              <div className="tw-text-xs tw-text-[#97A0AF] tw-pl-3 tw-py-1">
                Empty container
              </div>
            )}
          </div>
        </DragDropContext>
      )}
    </div>
  );
};
